import { type MouseEvent, type ReactNode, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from '@emotion/styled';

import CameraManager from '~/modules/Camera/CameraManager';
import { Menu } from '~/modules/Elements/AKUI/Menu';
import Loader from '~/modules/Elements/Loader';
import MenuWithLogo from '~/modules/Elements/MenuWithLogo';
import { nextValue } from '~/modules/Elements/Utils/indexes';
import useBackgroundMusic from '~/modules/hooks/useBackgroundMusic';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import useSmoothNavigate from '~/modules/hooks/useSmoothNavigate';

import {
  FpsCount,
  FPSCountSetting,
  GraphicSetting,
  GraphicsLevel,
  MobilePhoneModeSetting,
  useSettingValue,
} from '~/routes/Settings/SettingsState';

type MouseFocusableProps = {
  onMouseEnter?: (e: MouseEvent) => void;
};
function Settings() {
  useBackgroundMusic(false);
  const navigate = useSmoothNavigate();
  const goBack = () => navigate('');

  const { register, focusElement } = useKeyboardNav({ onBackspace: goBack });

  const withMouseFocus = <T extends object>(name: string, props: T): T & MouseFocusableProps => {
    const currentOnMouseEnter = (props as MouseFocusableProps).onMouseEnter;
    const onMouseEnter = (e: MouseEvent) => {
      focusElement(name, 'mouse');
      currentOnMouseEnter?.(e);
    };
    return { ...props, onMouseEnter };
  };

  const [graphicLevel, setGraphicLevel] = useSettingValue(GraphicSetting);
  const [fpsCount, setFpsCount] = useSettingValue(FPSCountSetting);
  const [mobilePhoneMode, setMobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);

  const [camera, setCamera] = useState<null | boolean>(CameraManager.getPermissionStatus());

  useEffect(() => {
    return CameraManager.addListener((status) => setCamera(status));
  }, []);

  const [isRequestInProgress, setIsRequestInProgress] = useState(false);

  const enableCamera = () => {
    setIsRequestInProgress(true);
    CameraManager.requestPermissions().then(() => setIsRequestInProgress(false));
  };

  let cameraDisplayValue: ReactNode = 'Disabled';

  if (isRequestInProgress) {
    cameraDisplayValue = <Loader size="0.9em" />;
  } else if (camera === null) {
    cameraDisplayValue = 'Click to enable';
  } else if (camera) {
    cameraDisplayValue = 'Enabled';
  }

  return (
    <MenuWithLogo
      supportedBrowsers={false}
      backgroundImage="/menu_background_blur.png"
      backgroundOverlay
      toolbar
      layout="plain">
      <Helmet>
        <title>Settings | fastKaraoke</title>
      </Helmet>

      <Shell>
        <Panel>
          <Title>Settings</Title>

          <Section>
            <SettingButton
              {...withMouseFocus(
                'graphics-level',
                register('graphics-level', () => setGraphicLevel(nextValue(GraphicsLevel, graphicLevel))),
              )}>
              <SettingRow>
                <SettingLabel>Graphics</SettingLabel>
                <SettingValue>{graphicLevel.toUpperCase()}</SettingValue>
              </SettingRow>
            </SettingButton>

            <SettingButton
              {...withMouseFocus(
                'fps-count-level',
                register('fps-count-level', () => setFpsCount(nextValue(FpsCount, fpsCount))),
              )}>
              <SettingRow>
                <SettingLabel>FPS Count</SettingLabel>
                <SettingValue>{fpsCount}</SettingValue>
              </SettingRow>
            </SettingButton>
          </Section>

          <Section>
            <Menu.Button
              {...withMouseFocus(
                'calibration-settings',
                register('calibration-settings', () => navigate('settings/calibration/')),
              )}
              size="small">
              Calibrate input lag
            </Menu.Button>

            <InfoText>If the sound is not synchronised with the lyrics, use this to compensate it.</InfoText>
          </Section>

          <Separator />

          <Section>
            <SettingButton
              {...withMouseFocus(
                'camera-access',
                register('camera-access', () => (camera ? CameraManager.disable() : enableCamera())),
              )}>
              <SettingRow>
                <SettingLabel>Enable camera mode</SettingLabel>
                <SettingValue>{cameraDisplayValue}</SettingValue>
              </SettingRow>
            </SettingButton>

            <InfoText>Record a timelapse video from singing. The recording is not sent nor stored anywhere.</InfoText>
          </Section>

          <Separator />

          <Section>
            <SettingButton
              {...withMouseFocus(
                'mobile-phone-mode',
                register('mobile-phone-mode', () => setMobilePhoneMode(!mobilePhoneMode)),
              )}>
              <SettingRow>
                <SettingLabel>Mobile Phone Mode</SettingLabel>
                <SettingValue>{mobilePhoneMode ? 'Yes' : 'No'}</SettingValue>
              </SettingRow>
            </SettingButton>

            <InfoText>Adjust the game to a smaller screen. Disables option to sing in duets.</InfoText>
          </Section>

          <Separator />

          <Actions>
            <Menu.Button {...withMouseFocus('back-button', register('back-button', goBack))}>
              Return To Song List
            </Menu.Button>
          </Actions>
        </Panel>
      </Shell>
    </MenuWithLogo>
  );
}

export default Settings;

const Shell = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const Panel = styled.div`
  border-radius: 8px;
  border: 1px solid var(--walky-border);
  border-top: 4px solid var(--walky-orange);
  background: var(--walky-surface-translucent);
  backdrop-filter: blur(18px);
  box-shadow: var(--walky-shadow-strong);
  color: var(--walky-ink);
  padding: 1.25rem 1.25rem 1.5rem;

  @media (min-width: 640px) {
    padding: 2rem;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const Separator = styled.hr`
  border: 0;
  border-top: 1px solid var(--walky-border);
  margin: 1rem 0;
`;

const Title = styled.h1`
  margin: 0 0 1.25rem;
  text-align: center;
  font-size: clamp(2.4rem, 5vw, 4rem);
  color: var(--walky-ink);
  letter-spacing: 0;
`;

const SettingButton = styled(Menu.Button)`
  width: 100%;
  padding-left: 1.25rem !important;
  padding-right: 1.25rem !important;
  min-height: 5.2rem;
  border-radius: 8px;

  &[data-focused='true'] span,
  &:hover span,
  &:focus-visible span {
    color: white;
  }

  @media (max-width: 520px) {
    min-height: 5.8rem;
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }
`;

const SettingRow = styled.div`
  width: 100%;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
  }
`;

const SettingLabel = styled.span`
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 520px) {
    white-space: normal;
  }
`;

const SettingValue = styled.span`
  font-weight: 800;
  text-transform: uppercase;
  margin-left: auto;
  white-space: nowrap;
  color: var(--walky-orange);

  @media (max-width: 520px) {
    margin-left: 0;
    white-space: normal;
  }
`;

const InfoText = styled.p`
  margin: 0.1rem 0 0.35rem;
  opacity: 0.86;
  font-size: 0.98rem;
  color: var(--walky-muted);
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;
